import {observer} from 'mobx-react-lite';
import React from 'react';

import {ErrorMessage, Loading} from '#';
import {PdfViewer} from '#/PdfViewer/PdfViewer';
import {PreviewDialog} from '#/PreviewDialog';
import {Warning} from '_/assets/images';
import {useDialogController} from '_/hooks';
import {useTemporaryDocumentPdfUrl} from '_/hooks/useRefreshableDocumentPdfUrl';
import {WizardStepProps} from '_/sharedComponents/CreateFromTemplateWizard';
import {openFileReadOnly, State} from '_/utilities';

import {DocumentTitle} from '../CreateFromTemplateWizard.styles';

import {StyledPreview, StyledWarning} from './Preview.styles';
import {PreviewWrapper} from './PreviewWrapper';

type PreviewProps = {
  documentTitle?: string;
  documentId: string;
  documentState: State;
  errorText: string;
};

type Props = {
  nextButtonDisabled?: boolean;
  isSubmitting?: boolean;
  documentId: string | undefined;
  documentState: State;
  errorText: string;
} & Omit<PreviewProps, 'documentId'> &
  WizardStepProps;

const Preview = ({
  documentTitle,
  documentId,
  documentState,
  errorText,
}: PreviewProps): React.ReactElement => {
  const {isVisible, showDialog, closeDialog} = useDialogController();
  const documentUrl = useTemporaryDocumentPdfUrl(documentId);

  const onOpenRequest = () =>
    openFileReadOnly({
      remotePath: documentUrl,
      handle: documentId,
      fileName: `${documentTitle}.pdf`,
    });

  const dialog = (
    <PreviewDialog
      file={documentUrl}
      isOpen={isVisible}
      width={600}
      onClose={closeDialog}
      onOpenRequest={onOpenRequest}
    />
  );

  switch (documentState) {
    case State.Success:
      return (
        <>
          {documentTitle ? (
            <DocumentTitle>{documentTitle}</DocumentTitle>
          ) : undefined}
          <div className='thumbnail'>
            <PdfViewer file={documentUrl} width={368} onExpand={showDialog} />
          </div>
          {dialog}
        </>
      );
    case State.Error:
      return (
        <StyledWarning>
          <Warning />
          <ErrorMessage message={errorText} />
        </StyledWarning>
      );
    default:
      return <Loading />;
  }
};

const UPreview = ({
  documentTitle,
  documentId,
  backLabel,
  nextLabel,
  onBackClick,
  documentState,
  onNextClick,
  nextButtonDisabled,
  isSubmitting,
  errorText,
}: Props): React.ReactElement => (
  <PreviewWrapper
    backLabel={backLabel}
    isSubmitting={isSubmitting}
    nextButtonDisabled={nextButtonDisabled ?? documentState === State.Loading}
    nextLabel={nextLabel}
    onBackClick={onBackClick}
    onNextClick={onNextClick}
  >
    <StyledPreview>
      {documentId ? (
        <Preview
          documentId={documentId}
          documentState={documentState}
          documentTitle={documentTitle}
          errorText={errorText}
        />
      ) : (
        <Loading />
      )}
    </StyledPreview>
  </PreviewWrapper>
);

export default observer(UPreview);
